import { useState, useEffect, useCallback } from 'react';
import {
  createDemoSession,
  getDemoSession,
  sendChatMessage,
  submitGovernanceRequest,
} from '../lib/api';

// Generate a unique visitor ID
function getVisitorId() {
  let visitorId = localStorage.getItem('cao_visitor_id');
  if (!visitorId) {
    visitorId = 'v_' + Math.random().toString(36).substring(2, 15);
    localStorage.setItem('cao_visitor_id', visitorId);
  }
  return visitorId;
}

export function useDemoSession() {
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Initialize or restore session
  const initSession = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Check for existing session
      const existingToken = sessionStorage.getItem('cao_demo_session');

      if (existingToken) {
        try {
          const { session: existingSession } = await getDemoSession(existingToken);
          // Include sessionToken in the session object (API response doesn't include it)
          const restoredSession = {
            ...existingSession,
            sessionToken: existingToken,
          };
          setSession(restoredSession);
          setMessages(existingSession.messages || []);
          setRequests(existingSession.requests || []);
          setIsLoading(false);
          return restoredSession;
        } catch (err) {
          // Session expired or invalid, create new one
          sessionStorage.removeItem('cao_demo_session');
        }
      }

      // Create new session
      const visitorId = getVisitorId();
      const { sessionToken, expiresAt } = await createDemoSession(visitorId);

      sessionStorage.setItem('cao_demo_session', sessionToken);

      const newSession = {
        sessionToken,
        expiresAt,
        messages: [],
        requests: [],
      };

      setSession(newSession);
      setMessages([]);
      setRequests([]);

      return newSession;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Send a chat message
  // displayContent: optional text to show in chat (for quick actions that send detailed prompts)
  const sendMessage = useCallback(async (message, displayContent = null) => {
    if (!session?.sessionToken) {
      throw new Error('No active session');
    }

    setIsLoading(true);
    setError(null);

    // Create user message with unique ID for tracking
    const messageId = Date.now().toString();
    const userMessage = {
      id: messageId,
      role: 'USER',
      content: displayContent || message,
      fullMessage: message, // Store full message for retry
      createdAt: new Date().toISOString(),
    };

    try {
      // Optimistically add user message (show displayContent if provided, otherwise show message)
      setMessages((prev) => [...prev, userMessage]);

      // Send the full message to API for processing
      const { response } = await sendChatMessage(session.sessionToken, message);

      // Add assistant response
      const assistantMessage = {
        role: 'ASSISTANT',
        content: response,
        createdAt: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, assistantMessage]);

      return response;
    } catch (err) {
      // Mark the user message as failed instead of removing it
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === messageId
            ? { ...msg, failed: true, error: err.message }
            : msg
        )
      );
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [session]);

  // Retry a failed message
  const retryMessage = useCallback(async (messageId) => {
    const failedMessage = messages.find((msg) => msg.id === messageId && msg.failed);
    if (!failedMessage) return;

    // Remove the failed message first
    setMessages((prev) => prev.filter((msg) => msg.id !== messageId));

    // Retry sending
    return sendMessage(failedMessage.fullMessage, failedMessage.content);
  }, [messages, sendMessage]);

  // Dismiss a failed message
  const dismissFailedMessage = useCallback((messageId) => {
    setMessages((prev) => prev.filter((msg) => msg.id !== messageId));
    setError(null);
  }, []);

  // Submit a governance request
  const submitRequest = useCallback(async (request) => {
    if (!session?.sessionToken) {
      throw new Error('No active session');
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await submitGovernanceRequest(session.sessionToken, request);

      // Add to requests list
      setRequests((prev) => [result.request, ...prev]);

      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [session]);

  // Clear session
  const clearSession = useCallback(() => {
    sessionStorage.removeItem('cao_demo_session');
    setSession(null);
    setMessages([]);
    setRequests([]);
  }, []);

  return {
    session,
    messages,
    requests,
    isLoading,
    error,
    initSession,
    sendMessage,
    retryMessage,
    dismissFailedMessage,
    submitRequest,
    clearSession,
  };
}

export default useDemoSession;
